import React, { useState, useEffect, useRef, useLayoutEffect } from 'react'
import { createPortal } from 'react-dom'
import ModalStack from 'aldoo-ra/Modal/ModalStack'
import TextBundle from 'aldoo-ra/CMS/text-bundle'
import { listContacts, listGroups } from '../Contacts/controller'
import Typography from 'aldoo-ra/Typography'
import Button from "aldoo-ra/Button"
import Input from "aldoo-ra/Input"
import Spinner from 'aldoo-ra/Spinner'
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  UsersIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const SEPARATOR_RE = /[\s,]/

// Renders `anchorContent` inline; portals children to <body> below the
// anchor's bounding rect so the suggestion list escapes any Modal /
// drawer overflow clip. Position recomputes on scroll + resize while
// `visible` is true.
function AnchoredAutocomplete({ anchorContent, visible, children }) {
    const anchorRef = useRef(null)
    const [pos, setPos] = useState(null)
    const [zIndex, setZIndex] = useState(9999)

    // Walk ancestors for the highest explicit z-index + consult ModalStack
    // so the dropdown always lands above the parent dialog / drawer
    // regardless of how deep it's nested. Mirrors DropdownSelect's logic.
    const computeParentZ = () => {
        let max = 0
        let el = anchorRef.current?.parentElement
        while (el && el !== document.body) {
            const z = parseInt(window.getComputedStyle(el).zIndex, 10)
            if (!Number.isNaN(z)) max = Math.max(max, z)
            el = el.parentElement
        }
        const top = ModalStack.getTop?.()
        if (top?.zIndex) max = Math.max(max, top.zIndex)
        return max
    }

    useLayoutEffect(() => {
        if (!visible || typeof window === "undefined") return
        const measure = () => {
            const el = anchorRef.current
            if (!el) return
            const r = el.getBoundingClientRect()
            setPos({ top: r.bottom + 4, left: r.left, width: r.width })
        }
        measure()
        setZIndex(computeParentZ() + 10)
        const opts = { passive: true, capture: true }
        window.addEventListener("scroll", measure, opts)
        window.addEventListener("resize", measure)
        return () => {
            window.removeEventListener("scroll", measure, opts)
            window.removeEventListener("resize", measure)
        }
    }, [visible])

    return (
        <div ref={anchorRef} className="relative">
            {anchorContent}
            {visible && pos && typeof document !== "undefined" && createPortal(
                <div
                    style={{
                        position: "fixed",
                        top: pos.top,
                        left: pos.left,
                        width: pos.width,
                        zIndex,
                    }}
                >
                    {children}
                </div>,
                document.body,
            )}
        </div>
    )
}

/**
 * UserAccessWidget
 *
 * A single input doubles as search and add: typing filters the list; when the
 * query is a new valid email an "Add to list" button appears. Multiple emails
 * separated by space or comma are committed automatically. Each access entry
 * renders as a chip with a remove button.
 *
 * Props:
 *   emails      — string[]  current access list
 *   onAdd       — async (email) => void
 *   onRemove    — async (email) => void
 *   readOnly    — bool (default false)
 *   loading     — bool  show spinner overlay while mutating
 */
export default function UserAccessWidget({ emails = [], onAdd, onRemove, readOnly = false, loading = false, hideHeader = false, maxEmails = 0, onUpgrade }) {
  const texts = TextBundle({ id: "transfer_share_dashboard" })
  const [query, setQuery] = useState('')
  const [addError, setAddError] = useState('')
  const [mutating, setMutating] = useState(false)
  const [contacts, setContacts] = useState([])
  const [groups, setGroups] = useState([])

  useEffect(() => {
    if (readOnly) return
    let alive = true
    listContacts()
      .then((res) => { if (alive && res?.result) setContacts(res.contacts || []) })
      .catch(() => {})
    listGroups()
      .then((res) => { if (alive && res?.result) setGroups(res.groups || []) })
      .catch(() => {})
    return () => { alive = false }
  }, [readOnly])

  const contactsById = new Map(contacts.map((c) => [c.userId, c]))

  const filtered = query
    ? emails.filter((e) => e.toLowerCase().includes(query.toLowerCase()))
    : emails

  const trimmedQuery = query.trim().toLowerCase()
  // Emails parsed from the query that are valid and not already in the list.
  const pendingAdds = query
    .split(SEPARATOR_RE)
    .map((t) => t.trim().toLowerCase())
    .filter((t) => EMAIL_RE.test(t) && !emails.includes(t))
  const limitReached = maxEmails > 0 && emails.length >= maxEmails
  const remainingSlots = maxEmails > 0 ? Math.max(0, maxEmails - emails.length) : Infinity
  const canAdd = pendingAdds.length > 0 && !limitReached

  // The token the user is currently typing (last separated chunk).
  const activeTokens = query.split(SEPARATOR_RE).map((t) => t.trim()).filter(Boolean)
  const activeToken = (activeTokens[activeTokens.length - 1] || '').toLowerCase()
  const suggestions = activeToken
    ? contacts
        .filter((c) => c.email && EMAIL_RE.test(c.email))
        .filter((c) => !emails.includes(c.email.toLowerCase()) && !pendingAdds.includes(c.email.toLowerCase()))
        .filter((c) =>
          c.email.toLowerCase().includes(activeToken) ||
          (c.name || '').toLowerCase().includes(activeToken)
        )
        .slice(0, 6)
    : []

  // Groups whose name matches the active token, carrying the unique member
  // emails that are not already in the access list.
  const groupSuggestions = activeToken
    ? groups
        .filter((g) => (g.name || '').toLowerCase().includes(activeToken))
        .map((g) => {
          const memberEmails = (g.members || [])
            .map((id) => contactsById.get(id)?.email)
            .filter((e) => e && EMAIL_RE.test(e))
            .map((e) => e.toLowerCase())
          const unique = [...new Set(memberEmails)].filter((e) => !emails.includes(e))
          return { groupId: g.groupId, name: g.name, emails: unique }
        })
        .filter((g) => g.emails.length > 0)
        .slice(0, 4)
    : []

  const clearActiveToken = () => {
    const rest = activeTokens.slice(0, -1)
    setQuery(rest.length ? rest.join(' ') + ' ' : '')
  }

  const pickContact = async (email) => {
    if (limitReached) return
    const e = email.trim().toLowerCase()
    if (!emails.includes(e)) {
      setMutating(true)
      try { await onAdd(e) } catch { setAddError(texts?.failedAddEmail) } finally { setMutating(false) }
    }
    clearActiveToken()
  }

  const pickGroup = async (group) => {
    if (limitReached) return
    const toAdd = group.emails.filter((e) => !emails.includes(e)).slice(0, remainingSlots)
    if (toAdd.length) {
      setMutating(true)
      try { for (const e of toAdd) await onAdd(e) } catch { setAddError(texts?.failedAddEmail) } finally { setMutating(false) }
    }
    clearActiveToken()
  }

  const commit = async (raw) => {
    if (limitReached) return
    const tokens = raw.split(SEPARATOR_RE).map((t) => t.trim().toLowerCase()).filter(Boolean)
    const toAdd = []
    const bad = []
    for (const t of tokens) {
      if (!EMAIL_RE.test(t)) { bad.push(t); continue }
      if (emails.includes(t) || toAdd.includes(t)) continue
      if (toAdd.length >= remainingSlots) continue
      toAdd.push(t)
    }
    if (toAdd.length) {
      setMutating(true)
      try {
        for (const email of toAdd) await onAdd(email)
      } catch {
        setAddError(texts?.failedAddEmail)
      } finally {
        setMutating(false)
      }
    }
    setAddError(bad.length ? `${texts?.enterValidEmail}: ${bad.join(', ')}` : '')
    setQuery('')
  }

  const handleChange = (e) => {
    setQuery(e.target.value)
    setAddError('')
  }

  const removePending = (email) => {
    const remaining = query
      .split(SEPARATOR_RE)
      .map((t) => t.trim())
      .filter((t) => t && t.toLowerCase() !== email)
    setQuery(remaining.join(' '))
  }

  const handleRemove = async (email) => {
    setMutating(true)
    try {
      await onRemove(email)
    } finally {
      setMutating(false)
    }
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Header */}
      {!hideHeader && (
        <div className="flex items-center gap-1.5">
          <UsersIcon className="h-4 w-4 text-text_secondary stroke-2" />
          <Typography variant="txt1_accent">{texts?.userAccess}</Typography>
          {emails.length > 0 && (
            <span className="text-xs text-text_secondary bg-neutral_1 border border-bordered rounded-full px-1.5 py-0.5 leading-none">
              {emails.length}
            </span>
          )}
        </div>
      )}

      {/* Search + add input */}
      {!readOnly && (
        <div className="py-1.5">
          {maxEmails > 0 && (
            <div className="flex items-center justify-center gap-3 mb-2 text-sm">
              <span className="text-text_secondary">{emails.length} / {maxEmails}</span>
              {onUpgrade && (
                <Button variant="text_btn_primary" onClick={onUpgrade}>
                  {texts?.needMore || 'Need more?'}
                </Button>
              )}
            </div>
          )}

          <AnchoredAutocomplete
            anchorContent={
              <Input
                variant="transfer_compact"
                autoFocus
                value={query}
                onChange={handleChange}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(query) } }}
                showClearButton
                icon={<MagnifyingGlassIcon className="h-4 w-4 text-text_secondary stroke-2 flex-shrink-0" />}
                placeholder={texts?.enterEmailsOrGroups}
                className="w-full"
                disabled={limitReached}
              />
            }
            visible={groupSuggestions.length > 0 || suggestions.length > 0}
          >
            <div className="bg-paper border border-bordered rounded-xl shadow-lg overflow-hidden max-h-[240px] overflow-y-auto">
                {groupSuggestions.map((g) => (
                  <button
                    key={`group-${g.groupId}`}
                    onClick={() => pickGroup(g)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-neutral_1/60 transition-colors"
                  >
                    <span className="flex items-center justify-center h-7 w-7 rounded-full bg-accent_1/10 text-accent_1 flex-shrink-0">
                      <UserGroupIcon className="h-4 w-4 stroke-2" />
                    </span>
                    <span className="flex flex-col min-w-0">
                      <span className="text-sm text-text truncate leading-tight">{g.name}</span>
                      <span className="text-xs text-text_secondary truncate leading-tight">
                        {texts?.group} · {g.emails.length} {g.emails.length === 1 ? texts?.member : texts?.members}
                      </span>
                    </span>
                  </button>
                ))}
                {suggestions.map((c) => (
                  <button
                    key={c.userId || c.email}
                    onClick={() => pickContact(c.email)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-neutral_1/60 transition-colors"
                  >
                    <span className="flex items-center justify-center h-7 w-7 rounded-full bg-primary/10 text-primary text-xs font-semibold flex-shrink-0">
                      {((c.name || c.email || '?')[0] || '?').toUpperCase()}
                    </span>
                    <span className="flex flex-col min-w-0">
                      {c.name && <span className="text-sm text-text truncate leading-tight">{c.name}</span>}
                      <span className="text-xs text-text_secondary truncate leading-tight">{c.email}</span>
                    </span>
                  </button>
                ))}
              </div>
          </AnchoredAutocomplete>

          {pendingAdds.length > 1 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {pendingAdds.map((email) => (
                <span
                  key={email}
                  className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 border border-primary/30 pl-3 pr-1.5 py-1 text-sm text-text"
                >
                  <span className="truncate max-w-[220px]">{email}</span>
                  <button
                    onClick={() => removePending(email)}
                    className="p-0.5 rounded-full hover:bg-error/10 text-text_secondary hover:text-error transition-colors flex-shrink-0"
                  >
                    <XMarkIcon className="h-3.5 w-3.5 stroke-2" />
                  </button>
                </span>
              ))}
            </div>
          )}

          {canAdd && (
            <Button
              variant="primary_s"
              onClick={() => commit(query)}
              disabled={mutating}
              className="mt-2 w-full"
            >
              {mutating ? <Spinner className="w-[14px] h-[14px]" /> : texts?.addToList}
            </Button>
          )}

          {addError && <p className="text-xs text-error px-1 mt-1">{addError}</p>}
        </div>
      )}

      {/* Email chips — reserved zone keeps the layout stable even when
          there are no recipients yet. */}
      <div className="rounded-xl border border-bordered border-dashed p-3 min-h-[80px] flex flex-wrap items-center justify-center gap-2 max-h-[380px] overflow-y-auto">
        {filtered.length > 0 ? (
          filtered.map((email) => (
            <span
              key={email}
              className="inline-flex items-center gap-1.5 rounded-full bg-neutral_1 border border-bordered pl-3 pr-1.5 py-1 text-sm text-text"
            >
              <span className="truncate max-w-[220px]">{email}</span>
              {!readOnly && (
                <button
                  onClick={() => handleRemove(email)}
                  className="p-0.5 rounded-full hover:bg-error/10 text-text_secondary hover:text-error transition-colors flex-shrink-0"
                  title={texts?.removeAccess}
                >
                  <XMarkIcon className="h-3.5 w-3.5 stroke-2" />
                </button>
              )}
            </span>
          ))
        ) : (
          // Empty-state copy: an authenticated share / receive link with no
          // emails means only the owner can open it. The old "Recipients"
          // label was ambiguous — users thought files were public until they
          // added someone. New copy spells out the actual state + the next
          // step.
          <span className="text-sm text-text_secondary text-center px-2">
            {texts?.onlyYouHaveAccess}
          </span>
        )}
      </div>

      {limitReached && !readOnly && (
        <Typography variant="txt2_dim" className="text-center text-error">
          {texts?.userAccessLimitReached}
        </Typography>
      )}

      {(loading || mutating) && (
        <div className="flex justify-center py-1">
          <Spinner className="w-[16px] h-[16px]" />
        </div>
      )}
    </div>
  )
}
